import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { LAT_LNG_BOUNDS } from "@miplatform/constants";
import { Box, BoxProps } from "@mui/material";
import {
  Map,
  MapMouseEvent,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { MiMarker } from "./MiMarker/MiMarker";

interface Position {
  lat: number;
  lng: number;
}

interface MiMapProps {
  containerProps?: BoxProps;
  children?: ReactNode;
  onCoordUpdate?: (position: Position) => void;
  onCoordChange?: (position: Position) => void;
  zoomFactor?: number;
  initialMarkerPosition?: Position | null;
  initialMapPosition?: Position;
  center?: google.maps.LatLngLiteral;
  mapOptions?: google.maps.MapOptions;
  enableClick?: boolean;
  showMarker?: boolean;
  mapTypeId?: { mapTypeId: string };
  mapContainerStyle?: CSSProperties;
}

const options: google.maps.MapOptions = {
  clickableIcons: false,
  zoomControl: true,
  restriction: {
    latLngBounds: LAT_LNG_BOUNDS,
    strictBounds: false,
  },
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const defaultMapPosition: Position = { lat: 39.8283, lng: -98.5795 };

export function MiMap({
  containerProps,
  children = null,
  onCoordUpdate,
  onCoordChange,
  zoomFactor = 5,
  initialMarkerPosition,
  initialMapPosition = defaultMapPosition,
  center,
  mapOptions = {},
  enableClick = true,
  showMarker = false,
  mapTypeId = { mapTypeId: "roadmap" },
  mapContainerStyle = { width: "100%", height: "100%" },
  ...rest
}: MiMapProps) {
  const map = useMap();
  const core = useMapsLibrary("core");
  const [markerPosition, setMarkerPosition] = useState<Position | null>(
    initialMarkerPosition || null
  );

  const hasMapLoaded = !!core;

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (!enableClick || !event.detail.latLng) {
        return;
      }

      const { lat, lng } = event.detail.latLng;

      if (showMarker) {
        setMarkerPosition({ lat, lng });
      }

      if (onCoordChange) {
        onCoordChange({ lat, lng });
      }
    },
    [enableClick, onCoordChange, showMarker]
  );

  useEffect(() => {
    if (map && core) {
      const bounds = new core.LatLngBounds(initialMapPosition);
      map.setCenter(bounds.getCenter());
    }
  }, [map, core, initialMapPosition]);

  useEffect(() => {
    if (map) {
      map.setZoom(zoomFactor);
    }
  }, [map, zoomFactor]);

  useEffect(() => {
    if (initialMarkerPosition) {
      setMarkerPosition(initialMarkerPosition);
    }
  }, [initialMarkerPosition]);

  return (
    <Box data-testid="mi-map" style={{ flex: 1 }} {...containerProps}>
      {hasMapLoaded ? (
        <Map
          defaultZoom={zoomFactor}
          defaultCenter={initialMarkerPosition || initialMapPosition}
          center={center}
          onClick={handleMapClick}
          {...{ options: { ...options, ...mapTypeId, ...mapOptions } }}
          {...rest}
        >
          {showMarker && markerPosition && (
            <MiMarker position={markerPosition} />
          )}
          {children}
        </Map>
      ) : (
        <Box>Configuration Not Found</Box>
      )}
    </Box>
  );
}
